import { memo, useMemo } from "react";
import { Button } from "react-bootstrap";

import { CodeHighlight } from "../../../../components/CodeHighlight";
import { CustomTextArea } from "../../../../components/CustomTextArea/CustomTextArea";
import { TrashIcon } from "../../../../components/icons/TrashIcon";
import type { MessagesState, MessageType } from "../../types";
const isMacOS = navigator.platform.includes("Mac");

interface FunctionMessageProps {
  message: MessageType;
  isLoading: boolean;
  messagesList: MessagesState;
  disabledSubmit?: boolean;
  deleteMessage: (id: number | string) => void;
  sendQuestion: (question: string | null) => void;
  editMessage: (message: MessageType) => void;
}

const FunctionMessage: React.FC<FunctionMessageProps> = memo(
  ({
    message,
    messagesList,
    isLoading,
    disabledSubmit,
    deleteMessage,
    sendQuestion,
    editMessage,
  }) => {
    const handleEditMessage = (value: any, message: any) => {
      const resultMessage = {
        ...message,
        choices: [
          {
            ...message.choices[message.selectedChoice],
            content: value,
          },
        ],
      };

      editMessage(resultMessage);
    };

    const handleDeleteMessage = () => {
      deleteMessage(message.id);
    };

    const handleSubmit = () => {
      sendQuestion(null);
    };

    const defaultText = useMemo(() => {
      return {
        value: "",
      };
    }, []);

    const onEnter = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault();
        handleSubmit();
      }
    };

    return (
      <div className="playground-message function-message">
        <div className="playground-message-header">
          <div className="playground-message-role">
            {message.choices[message.selectedChoice].role}
          </div>
          <div className="playground-message-controls">
            <Button
              variant="outline-light"
              data-testid="delete"
              onClick={handleDeleteMessage}
            >
              <TrashIcon />
            </Button>
          </div>
        </div>
        {(message.choices[message.selectedChoice]?.tool_calls || []).map(
          tool => {
            const code = `  ${tool.function.name}(
    ${tool.function.arguments}
  )`;
            const responseMessage = messagesList[tool.id];
            return (
              <div className="function-message-content" key={tool.id}>
                <CodeHighlight code={code} codeType="js" />
                <div className="divider" />
                <div className="function-message-editor">
                  <h6>response</h6>
                  <div className="d-flex align-items-center justify-content-between function-message-response">
                    <div className="function-message-editor">
                      <CustomTextArea
                        defaultText={defaultText}
                        onChange={value =>
                          handleEditMessage(value, responseMessage)
                        }
                        placeholder={`Enter output e.g. { "success": true }`}
                        value={
                          responseMessage?.choices[message.selectedChoice]
                            ?.content || ""
                        }
                        wrapperClassName="ps-0 monospace"
                        placeholderClassName="ms-0"
                        onEnter={onEnter}
                      />
                    </div>
                    <Button
                      className="mt-auto mb-2"
                      variant="primary"
                      disabled={disabledSubmit || isLoading}
                      onClick={handleSubmit}
                    >
                      Submit {`${isMacOS ? "⌘" : "Ctrl"} `} ↵
                    </Button>
                  </div>
                </div>
              </div>
            );
          },
        )}
      </div>
    );
  },
);

export { FunctionMessage };
